import {
  allocateGlMarketCharged,
  autoPrimaryPremium as computeAutoPrimaryPremium,
  selectedAdequate,
} from "./adequatePremiums";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Collapse,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import AutoLimitGuideTab from "components/pricing/xsRating/AutoLimitGuideTab";
import XsRatingDetails from "components/pricing/xsRating/XsRatingDetails";
import XsRatingResults from "components/pricing/xsRating/XsRatingResults";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as programActions from "store/actions/input/program/programActions";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as baseLayerBurnStore from "store/baseLayerBurn";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const ENGINE = "united_es_xs_v1";

const FONT_SIZE = "0.875rem";

const cellSx = { fontSize: FONT_SIZE, py: 0.5, px: 1.5 };
const headSx = {
  fontSize: FONT_SIZE,
  fontWeight: 600,
  py: 0.75,
  px: 1.5,
  whiteSpace: "nowrap",
};
const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: FONT_SIZE,
    textAlign: "right",
    py: "4px",
    px: 1,
  },
};

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const HAZARD_OPTIONS = ["Low", "Medium", "High"];
const LIQUOR_HAZARD_OPTIONS = ["Medium", "High"];
const GUIDELINE_HAZARDS = {
  premOps: "Medium",
  pco: "Medium",
  liquor: "High",
  auto: "Medium",
};

const IRPM_LINES = [
  { key: "irpmClassFit", label: "Class Fit" },
  { key: "irpmLossHistory", label: "Loss History" },
  { key: "irpmTermsConditions", label: "Terms & Conditions" },
];

const IRPM_MIN = -0.1;
const IRPM_MAX = 0.3;

const fmtAmt = (v) => (v == null ? "—" : `$${Math.round(v).toLocaleString()}`);

const SectionBar = ({ label, summary, summaryColor, open, onToggle }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    justifyContent={"space-between"}
    onClick={onToggle}
    sx={{
      backgroundColor: "grey.100",
      cursor: "pointer",
      px: 2,
      py: 1.25,
      borderRadius: 1,
    }}
  >
    <Typography fontSize={FONT_SIZE} fontWeight={700} color={"text.secondary"}>
      {label}
    </Typography>
    <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
      {summary != null && (
        <Typography
          fontSize={FONT_SIZE}
          fontWeight={600}
          color={summaryColor ?? "text.secondary"}
        >
          {summary}
        </Typography>
      )}
      {open ? (
        <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      )}
    </Stack>
  </Stack>
);

const SubSectionTitle = ({ children }) => (
  <Typography
    variant={"overline"}
    color={"text.secondary"}
    sx={{ letterSpacing: 1, lineHeight: 1, fontSize: "0.7rem" }}
  >
    {children}
  </Typography>
);

const ReadOnlyCell = ({ value, secondary, bold }) => (
  <TableCell
    align={"right"}
    sx={{
      ...cellSx,
      color: secondary ? "text.secondary" : "text.primary",
      fontWeight: bold ? 700 : 400,
    }}
  >
    {value ?? "—"}
  </TableCell>
);

// --- Adequate Premiums Section ---

const SublinePremiumsSection = ({
  data,
  onUpdate,
  exposureRated,
  autoMarketCharged,
}) => {
  const [open, setOpen] = useState(true);

  const glSublines = [
    { label: "GL Premises", adequateKey: "premOpsAdequate", erpKey: "premOps" },
    { label: "GL Products", adequateKey: "pcoAdequate", erpKey: "pco" },
    { label: "Liquor", adequateKey: "liquorAdequate", erpKey: "liquor" },
  ];

  // GL MCP: user enters Total GL, distributed proportionally by GL ERP weight.
  const marketChargedGlTotal = data?.marketChargedGlTotal ?? null;
  const totalGlErpRaw = glSublines.reduce(
    (s, sl) => s + (exposureRated[sl.erpKey] ?? 0),
    0
  );
  const glMcpBySubline = allocateGlMarketCharged(
    exposureRated,
    marketChargedGlTotal
  );

  const totalGlErp = totalGlErpRaw || null;
  // Selected = override ?? MAX(exposure-rated, market-charged); see adequatePremiums.
  const glSelectedFor = (sl) =>
    selectedAdequate(
      exposureRated[sl.erpKey],
      glMcpBySubline[sl.erpKey],
      data?.[sl.adequateKey]
    );
  const totalGlSelected =
    glSublines.reduce((s, sl) => s + glSelectedFor(sl), 0) || null;

  // Auto: always from Auto Pricing tab, not user-editable. Same MAX rule.
  const autoErp = exposureRated.auto;
  const autoSelected =
    autoErp == null && autoMarketCharged == null
      ? null
      : Math.max(autoErp ?? 0, autoMarketCharged ?? 0);

  const totalErp = totalGlErpRaw + (autoErp ?? 0) || null;
  const totalMcp =
    marketChargedGlTotal != null || autoMarketCharged != null
      ? (marketChargedGlTotal ?? 0) + (autoMarketCharged ?? 0)
      : null;
  const totalSelected = (totalGlSelected ?? 0) + (autoSelected ?? 0) || null;

  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Adequate Premiums"}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <TableContainer>
          <Table size={"small"} sx={{ mt: 0.5 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={headSx}>{"Subline"}</TableCell>
                <TableCell
                  sx={{ ...headSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {"Exposure Rated Premium"}
                </TableCell>
                <TableCell
                  sx={{ ...headSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {"Market Charged Premium"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Selected Adequate ($)"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* GL sublines */}
              {glSublines.map(({ label, adequateKey, erpKey }) => (
                <TableRow key={adequateKey} hover>
                  <TableCell sx={cellSx}>{label}</TableCell>
                  <ReadOnlyCell
                    value={fmtAmt(exposureRated[erpKey])}
                    secondary
                  />
                  <ReadOnlyCell
                    value={fmtAmt(glMcpBySubline[erpKey])}
                    secondary
                  />
                  <TableCell align={"right"} sx={{ ...cellSx, minWidth: 150 }}>
                    <PrettyNumberTextField
                      hiddenLabel
                      size={"small"}
                      variant={"filled"}
                      value={data?.[adequateKey] ?? null}
                      placeholder={String(
                        Math.round(
                          selectedAdequate(
                            exposureRated[erpKey],
                            glMcpBySubline[erpKey]
                          )
                        )
                      )}
                      onChangeNumber={(v) => onUpdate(adequateKey, v)}
                      sx={inputSx}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {/* Total GL row — MCP editable here */}
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                  {"Total GL"}
                </TableCell>
                <ReadOnlyCell value={fmtAmt(totalGlErp)} bold />
                <TableCell align={"right"} sx={{ ...cellSx, minWidth: 150 }}>
                  <PrettyNumberTextField
                    hiddenLabel
                    size={"small"}
                    variant={"filled"}
                    value={marketChargedGlTotal}
                    placeholder={"0"}
                    onChangeNumber={(v) => onUpdate("marketChargedGlTotal", v)}
                    sx={inputSx}
                  />
                </TableCell>
                <ReadOnlyCell value={fmtAmt(totalGlSelected)} bold />
              </TableRow>

              {/* Auto row — always from Auto Pricing tab */}
              <TableRow hover>
                <TableCell
                  sx={{
                    ...cellSx,
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  {"Auto"}
                </TableCell>
                <ReadOnlyCell value={fmtAmt(autoErp)} secondary />
                <ReadOnlyCell value={fmtAmt(autoMarketCharged)} secondary />
                <ReadOnlyCell value={fmtAmt(autoSelected)} secondary />
              </TableRow>

              {/* Grand total row — fully read-only */}
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                  {"Total"}
                </TableCell>
                <ReadOnlyCell value={fmtAmt(totalErp)} bold />
                <ReadOnlyCell value={fmtAmt(totalMcp)} bold />
                <ReadOnlyCell value={fmtAmt(totalSelected)} bold />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Stack>
  );
};

// --- Hazard Grade Section ---

const HazardGradeSection = ({
  data,
  onUpdate,
  guidelineHazards,
  layerAttachment,
}) => {
  const [open, setOpen] = useState(true);
  const sublines = [
    {
      key: "premOps",
      label: "GL Prem Ops",
      hazardKey: "premOpsHazard",
      attachmentKey: "premOpsAttachment",
      options: HAZARD_OPTIONS,
    },
    {
      key: "pco",
      label: "GL Products (PCO)",
      hazardKey: "pcoHazard",
      attachmentKey: "pcoAttachment",
      options: HAZARD_OPTIONS,
    },
    {
      key: "liquor",
      label: "Liquor",
      hazardKey: "liquorHazard",
      attachmentKey: "liquorAttachment",
      options: LIQUOR_HAZARD_OPTIONS,
    },
    {
      key: "auto",
      label: "Auto",
      hazardKey: "autoHazard",
      attachmentKey: "autoAttachment",
      options: HAZARD_OPTIONS,
    },
  ];
  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Hazard Grade Overrides"}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <TableContainer>
          <Table size={"small"} sx={{ mt: 0.5 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={headSx}>{"Subline"}</TableCell>
                <TableCell
                  sx={{ ...headSx, color: "text.secondary" }}
                  align={"right"}
                >
                  {"Guideline"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Override"}
                </TableCell>
                <TableCell sx={headSx} align={"right"}>
                  {"Attachment (ground up)"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sublines.map(
                ({ key, label, hazardKey, attachmentKey, options }) => {
                  const guideline =
                    guidelineHazards?.[key] ?? GUIDELINE_HAZARDS[key];
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={cellSx}>{label}</TableCell>
                      <ReadOnlyCell value={guideline} secondary />
                      <TableCell
                        align={"right"}
                        sx={{ ...cellSx, minWidth: 120 }}
                      >
                        <TextField
                          select
                          size={"small"}
                          variant={"filled"}
                          fullWidth
                          hiddenLabel
                          value={
                            data?.[hazardKey] ??
                            guideline ??
                            options[Math.floor(options.length / 2)]
                          }
                          onChange={(e) => onUpdate(hazardKey, e.target.value)}
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: FONT_SIZE,
                              py: "4px",
                              px: 1,
                            },
                          }}
                        >
                          {options.map((h) => (
                            <MenuItem
                              key={h}
                              value={h}
                              sx={{ fontSize: FONT_SIZE }}
                            >
                              {h}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell
                        align={"right"}
                        sx={{ ...cellSx, minWidth: 130 }}
                      >
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          fullWidth
                          value={
                            data?.[attachmentKey] ??
                            layerAttachment ??
                            1_000_000
                          }
                          onChangeNumber={(v) => onUpdate(attachmentKey, v)}
                          sx={inputSx}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Stack>
  );
};

// --- IRPM Line ---

const IrpmLine = ({ line, data, onUpdate }) => {
  const decimal = data?.[line.key];
  const display = decimal != null ? decimal * 100 : null;
  return (
    <Grid container alignItems={"center"} spacing={1.5}>
      <Grid item xs={8}>
        <Typography fontSize={"0.825rem"}>{line.label}</Typography>
      </Grid>
      <Grid item xs={4}>
        <PrettyNumberTextField
          {...TF}
          hiddenLabel
          placeholder={"%"}
          value={display}
          onChangeNumber={(v) => onUpdate(line.key, v != null ? v / 100 : null)}
        />
      </Grid>
    </Grid>
  );
};

// --- IRPM Section (matches GL Pricing IrpmSection) ---

const IrpmSection = ({ data, onUpdate, open, onToggle }) => {
  const totalDecimal = IRPM_LINES.reduce(
    (acc, l) => acc + Number(data?.[l.key] ?? 0),
    0
  );
  const cappedDecimal = Math.max(IRPM_MIN, Math.min(IRPM_MAX, totalDecimal));
  const totalPct = totalDecimal * 100;
  const cappedPct = cappedDecimal * 100;
  const isCapped =
    totalDecimal < IRPM_MIN - 1e-9 || totalDecimal > IRPM_MAX + 1e-9;
  const hasAny = totalDecimal !== 0;

  const summary = hasAny ? `Total: ${cappedPct.toFixed(2)}%` : null;
  const summaryColor = isCapped
    ? "error.main"
    : cappedDecimal < 0
    ? "success.main"
    : "warning.main";

  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Individual Risk Premium Modifications (IRPM)"}
        summary={summary}
        summaryColor={hasAny ? summaryColor : undefined}
        open={open}
        onToggle={onToggle}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Stack spacing={1} sx={{ pt: 1, pb: 1, px: 1 }}>
          {IRPM_LINES.map((line) => (
            <IrpmLine
              key={line.key}
              line={line}
              data={data}
              onUpdate={onUpdate}
            />
          ))}
          <Grid
            container
            alignItems={"center"}
            spacing={1.5}
            sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1 }}
          >
            <Grid item xs={8}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Typography fontSize={"0.85rem"} fontWeight={600}>
                  {"Total Modification"}
                </Typography>
                <Typography
                  fontSize={"0.75rem"}
                  color={isCapped ? "error.main" : "text.secondary"}
                >
                  {isCapped
                    ? `(capped from ${totalPct.toFixed(2)}%)`
                    : "Cap: −10% to +30%"}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Typography
                fontSize={"0.875rem"}
                fontWeight={700}
                color={
                  isCapped
                    ? "error.main"
                    : cappedDecimal < 0
                    ? "success.main"
                    : cappedDecimal > 0
                    ? "warning.main"
                    : "text.primary"
                }
              >
                {`${cappedPct.toFixed(2)}%`}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
    </Stack>
  );
};

// Participation honors a manual override entered on the XS screen; otherwise it
// falls back to the active layer's quota share (shareOfLine, a percentage) so a
// quota share selected on Layer Pricing carries through to the excess rating.
// Returned as a fraction to match the engine's `participation` input.
export function resolveXsParticipation(manualParticipation, layerShareOfLine) {
  if (manualParticipation != null) {
    return manualParticipation;
  }
  if (layerShareOfLine != null) {
    return layerShareOfLine / 100;
  }
  return null;
}

// --- Policy Parameters & Adjustments Section ---

const PolicyParamsSection = ({
  data,
  onUpdate,
  layerLimit,
  onUpdateLayerLimit,
  layerShareOfLine,
}) => {
  const [open, setOpen] = useState(true);
  const [irpmOpen, setIrpmOpen] = useState(false);

  const seededParticipation = resolveXsParticipation(
    data?.participation,
    layerShareOfLine
  );

  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Policy Parameters & Adjustments"}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Stack spacing={2} sx={{ pt: 2, pb: 1, px: 1 }}>
          <Stack spacing={0.75}>
            <SubSectionTitle>{"Policy Parameters"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Policy Limit ($)"}
                  value={layerLimit ?? data?.policyLimit ?? null}
                  placeholder={"5,000,000"}
                  onChangeNumber={(v) => {
                    onUpdateLayerLimit(v);
                    onUpdate("policyLimit", v);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Participation (%)"}
                  value={
                    seededParticipation != null
                      ? seededParticipation * 100
                      : null
                  }
                  placeholder={"100"}
                  onChangeNumber={(v) =>
                    onUpdate("participation", v != null ? v / 100 : null)
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Min Per Million ($)"}
                  value={data?.minPerMillion ?? null}
                  placeholder={"2,000"}
                  onChangeNumber={(v) => onUpdate("minPerMillion", v)}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Premium Adjustments"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Manual Premium Adjustment ($)"}
                  value={data?.manualAdjustment ?? null}
                  onChangeNumber={(v) => onUpdate("manualAdjustment", v)}
                />
              </Grid>
              <Grid item xs={4}>
                <PrettyNumberTextField
                  {...TF}
                  label={"TRIA (%)"}
                  value={data?.triaPct != null ? data.triaPct * 100 : null}
                  onChangeNumber={(v) =>
                    onUpdate("triaPct", v != null ? v / 100 : null)
                  }
                />
              </Grid>
            </Grid>
          </Stack>

          <IrpmSection
            data={data}
            onUpdate={onUpdate}
            open={irpmOpen}
            onToggle={() => setIrpmOpen((v) => !v)}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
};

const SUB_TABS = ["XS Rating", "XS Rating Details", "Auto Limit Guide"];

const XsRating = () => {
  const dispatch = useDispatch();
  const data = useSelector(programSelectors.getXsRating);
  const result = useSelector(programSelectors.getXsRatingResult);
  const xsRatingAutoCalculate = useSelector(
    programSelectors.getXsRatingAutoCalculate
  );
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );
  const glPricing = useSelector(baseLayerBurnStore.selectBaseLayerBurn);
  const autoResult = useSelector(programSelectors.getAutoPricingResult);
  const activeLayer = useSelector(pricingSelectors.getActiveLayer);
  const resolvedProgram = useSelector(programSelectors.selectResolvedProgram);
  const proRataFactor = resolvedProgram?.proRataAdjustment
    ? resolvedProgram.proRataAdjustment / 100
    : 1;

  // GL pricing exposure-rated premiums and hazard suggestions
  const glTotals = glPricing?.pricing?.values?.totals;
  const glSuggestedHazards =
    glPricing?.pricing?.values?.suggested_xs_hazards ?? {};

  // Auto: use selected rates (mirrors AutoPricingResults selectedPremium)
  const autoRecords = autoResult?.values?.records ?? [];
  const autoSelectedRates = autoResult?.selectedRates ?? {};
  const autoExposureTotal =
    autoRecords.length > 0
      ? Math.round(
          autoRecords
            .filter((r) => r.count > 0)
            .reduce(
              (s, r) =>
                s + r.count * (autoSelectedRates[r.key] ?? r.guideline_rate),
              0
            )
        )
      : null;
  const autoMarketCharged =
    autoResult?.values?.totals?.market_charged_premium ?? null;

  // Guideline hazard grades: GL engine computes from dominant class code and stores in Redux;
  // auto engine stores its grade in its result. Both update without XS Calculate.
  const guidelineHazards = {
    premOps: glSuggestedHazards.premOps ?? GUIDELINE_HAZARDS.premOps,
    pco: glSuggestedHazards.pco ?? GUIDELINE_HAZARDS.pco,
    liquor: glSuggestedHazards.liquor ?? GUIDELINE_HAZARDS.liquor,
    auto:
      autoResult?.values?.inputs?.auto_hazard_grade ?? GUIDELINE_HAZARDS.auto,
  };

  // The engine prices auto at guideline rates; the UW's selected rates override
  // them (autoExposureTotal). Scale the engine's grossed-up auto ERP to the
  // selected exposure-rating premium so XS Rating matches what Auto Pricing shows.
  const autoGuidelineErp =
    autoResult?.values?.totals?.exposure_rating_premium ?? null;
  const autoSelectedScale =
    autoGuidelineErp && autoExposureTotal != null
      ? autoExposureTotal / autoGuidelineErp
      : 1;
  const exposureRated = {
    premOps: glTotals?.prem_ops_technical ?? null,
    pco: glTotals?.pco ?? null,
    liquor: glTotals?.liquor ?? null,
    auto:
      autoResult?.values?.totals?.xs_exposure_rated_premium != null
        ? autoResult.values.totals.xs_exposure_rated_premium *
          autoSelectedScale *
          proRataFactor
        : null,
  };
  // Auto 1x1 primary premium (auto FSES/ISO band-1 base): MAX(exposure-rating premium
  // at selected rates, market-charged 1x1) — Excel MAX(K15, K14). The engine computes
  // the market-charged 1x1 (limit-ILF restated to $1M, then x band-1 hazard ILF).
  const autoErpSelected =
    (autoExposureTotal ?? autoGuidelineErp) * proRataFactor;
  const autoMarketComparand =
    autoResult?.values?.totals?.market_charged_1x1 ?? null;
  const autoPrimaryPremium = computeAutoPrimaryPremium(
    autoErpSelected,
    autoMarketComparand
  );

  // Policy limit + attachment seeded from the active layer (Layer Pricing).
  const layerLimit = activeLayer?.limit ?? null;
  const layerAttachment = activeLayer?.attachment ?? null;
  const onUpdateLayerLimit = useCallback(
    (v) => {
      if (activeLayer?.id != null) {
        dispatch(
          pricingActions.updateLayer({
            layerId: activeLayer.id,
            values: { limit: v },
          })
        );
      }
    },
    [dispatch, activeLayer]
  );

  const [subTab, setSubTab] = useState(0);
  const [error, setError] = useState(null);

  const [
    _triggerPricing,
    { isFetching },
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  const onUpdate = useCallback(
    (field, value) => dispatch(programActions.updateXsRating(field, value)),
    [dispatch]
  );

  const handleCalculate = async () => {
    setError(null);
    const freshAutoAdequate = autoExposureTotal;
    const freshPremOpsErp = exposureRated.premOps;
    const freshPcoErp = exposureRated.pco;
    const freshLiquorErp = exposureRated.liquor;
    const freshIsoTierPremOps = glSuggestedHazards.isoTierPremOps ?? null;
    const freshIsoTierPco = glSuggestedHazards.isoTierPco ?? null;
    const freshPredominantState =
      submission?.glLimits?.predominant_state ?? null;

    // Excel keeps two hazards per subline:
    //   Guideline (B10:B13)  → drives FSES Technical + ISO Technical
    //   Override  (C10:C13)  → drives FSES Guideline (with IRPM applied to GL+Liquor)
    // Override falls back to the guideline value if the user hasn't customised.
    const resolvedHazards = {
      premOpsHazardGuideline: guidelineHazards.premOps ?? null,
      pcoHazardGuideline: guidelineHazards.pco ?? null,
      liquorHazardGuideline: guidelineHazards.liquor ?? null,
      autoHazardGuideline: guidelineHazards.auto ?? null,
      premOpsHazardOverride:
        data?.premOpsHazard ?? guidelineHazards.premOps ?? null,
      pcoHazardOverride: data?.pcoHazard ?? guidelineHazards.pco ?? null,
      liquorHazardOverride:
        data?.liquorHazard ?? guidelineHazards.liquor ?? null,
      autoHazardOverride: data?.autoHazard ?? guidelineHazards.auto ?? null,
    };
    // Persist the resolved override only when the user hasn't already set one,
    // so future Calculates don't drift if the GL guideline later changes.
    for (const subline of ["premOps", "pco", "liquor", "auto"]) {
      const overrideKey = `${subline}Hazard`;
      const value = resolvedHazards[`${subline}HazardOverride`];
      if (value != null && data?.[overrideKey] == null) {
        dispatch(programActions.updateXsRating(overrideKey, value));
      }
    }

    // Auto ERP comes from the Auto engine output. If Auto Pricing hasn't
    // been run yet, bail out — don't fall back silently.
    if (autoExposureTotal != null && exposureRated.auto == null) {
      setError(
        "Auto Exposure Rated Premium is missing. Run Auto Pricing first "
      );
      dispatch(programActions.setXsRatingResult(null));
      return;
    }
    const freshAutoErp = exposureRated.auto;

    dispatch(programActions.updateXsRating("premOpsErp", freshPremOpsErp));
    dispatch(programActions.updateXsRating("pcoErp", freshPcoErp));
    dispatch(programActions.updateXsRating("liquorErp", freshLiquorErp));
    dispatch(programActions.updateXsRating("autoErp", freshAutoErp));
    dispatch(
      programActions.updateXsRating("isoTierPremOps", freshIsoTierPremOps)
    );
    dispatch(programActions.updateXsRating("isoTierPco", freshIsoTierPco));
    dispatch(
      programActions.updateXsRating("predominantState", freshPredominantState)
    );

    // Selected Adequate = override ?? MAX(exposure-rated, allocated market-charged)
    // — Excel D2 = MAX(C2,B2). Not persisted, so the default stays live.
    const glErp = {
      premOps: freshPremOpsErp,
      pco: freshPcoErp,
      liquor: freshLiquorErp,
    };
    const glMcp = allocateGlMarketCharged(
      glErp,
      data?.marketChargedGlTotal ?? null
    );
    const resolvedAdequates = {
      premOpsAdequate: selectedAdequate(
        freshPremOpsErp,
        glMcp.premOps,
        data?.premOpsAdequate
      ),
      pcoAdequate: selectedAdequate(freshPcoErp, glMcp.pco, data?.pcoAdequate),
      liquorAdequate: selectedAdequate(
        freshLiquorErp,
        glMcp.liquor,
        data?.liquorAdequate
      ),
      autoAdequate: selectedAdequate(
        freshAutoAdequate,
        autoMarketCharged,
        data?.autoAdequate
      ),
    };

    const freshSubmission = {
      ...submission,
      xsRating: {
        ...submission.xsRating,
        ...resolvedAdequates,
        // Send the same resolved Policy Limit the field shows: it can come from
        // the layer (layerLimit) rather than only from typing into the XS field.
        policyLimit: layerLimit ?? data?.policyLimit ?? null,
        // Seed participation from the layer's quota share (shareOfLine) so a
        // quota share selected on Layer Pricing carries through to the rating.
        // A manual override entered on the XS screen still wins.
        participation: resolveXsParticipation(
          data?.participation,
          activeLayer?.shareOfLine ?? null
        ),
        // Per-subline attachment: the UW override wins, else seed from the
        // layer's attachment (Layer Pricing), else $1M (engine default).
        premOpsAttachment: data?.premOpsAttachment ?? layerAttachment ?? null,
        pcoAttachment: data?.pcoAttachment ?? layerAttachment ?? null,
        liquorAttachment: data?.liquorAttachment ?? layerAttachment ?? null,
        autoAttachment: data?.autoAttachment ?? layerAttachment ?? null,
        ...(freshPremOpsErp != null && { premOpsErp: freshPremOpsErp }),
        ...(freshPcoErp != null && { pcoErp: freshPcoErp }),
        ...(freshLiquorErp != null && { liquorErp: freshLiquorErp }),
        ...(freshAutoErp != null && { autoErp: freshAutoErp }),
        ...(autoPrimaryPremium != null && {
          autoPrimaryPremium,
        }),
        ...resolvedHazards,
        isoTierPremOps: freshIsoTierPremOps,
        isoTierPco: freshIsoTierPco,
        predominantState: freshPredominantState,
      },
    };

    try {
      const response = await _triggerPricing({
        engine: ENGINE,
        metric: "program_pricing",
        submission: freshSubmission,
      });
      if (response?.data?.failure) {
        setError(response.data.failure);
        dispatch(programActions.setXsRatingResult(null));
      } else {
        dispatch(
          programActions.setXsRatingResult(response?.data?.values ?? null)
        );
      }
    } catch (err) {
      setError(err?.message ?? "Pricing request failed.");
      dispatch(programActions.setXsRatingResult(null));
    }
  };

  const handleCalculateRef = useRef(handleCalculate);
  handleCalculateRef.current = handleCalculate;

  useEffect(() => {
    if (!xsRatingAutoCalculate) return;
    dispatch(programActions.setXsRatingAutoCalculate(false));
    handleCalculateRef.current();
  }, [xsRatingAutoCalculate, dispatch]);

  return (
    <Disabled ifReadOnly>
      <Component title={"XS Rating"} isFetching={isFetching}>
        {/* Excel-style sub-tab strip */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={subTab}
            onChange={(_, v) => setSubTab(v)}
            variant={"scrollable"}
            scrollButtons={"auto"}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                fontSize: "0.8rem",
                textTransform: "none",
                fontWeight: 500,
                px: 2,
                py: 0.75,
              },
            }}
          >
            {SUB_TABS.map((label) => (
              <Tab key={label} label={label} disableRipple />
            ))}
          </Tabs>
        </Box>

        {/* Tab 0 — Inputs + Calculate + Summary */}
        {subTab === 0 && (
          <Stack direction={"column"} gap={2} sx={{ pr: 2 }}>
            <PolicyParamsSection
              data={data}
              onUpdate={onUpdate}
              layerLimit={layerLimit}
              onUpdateLayerLimit={onUpdateLayerLimit}
              layerShareOfLine={activeLayer?.shareOfLine ?? null}
            />
            <SublinePremiumsSection
              data={data}
              onUpdate={onUpdate}
              exposureRated={exposureRated}
              autoMarketCharged={autoMarketCharged}
            />
            <HazardGradeSection
              data={data}
              layerAttachment={layerAttachment}
              onUpdate={onUpdate}
              guidelineHazards={guidelineHazards}
            />

            <Stack
              direction={"row"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              spacing={2}
            >
              {error && (
                <Alert severity={"error"} sx={{ width: "100%" }}>
                  {error}
                </Alert>
              )}
              <Button onClick={handleCalculate} isLoading={isFetching}>
                {"Calculate"}
              </Button>
            </Stack>

            {result && (
              <>
                <Divider />
                <XsRatingResults
                  values={result}
                  data={data}
                  onUpdate={onUpdate}
                />
              </>
            )}
          </Stack>
        )}

        {/* Tab 1 — Band breakdown + what-if */}
        {subTab === 1 &&
          (result ? (
            <XsRatingDetails values={result} />
          ) : (
            <Typography
              variant={"body2"}
              color={"text.secondary"}
              sx={{ p: 1 }}
            >
              {"Run Calculate on the XS Rating tab first."}
            </Typography>
          ))}

        {/* Tab 2 — Auto Limit-Attachment Guide */}
        {subTab === 2 && <AutoLimitGuideTab />}
      </Component>
    </Disabled>
  );
};

export default XsRating;
